import { useCallback, useMemo } from 'react';

import type { GridColumnVisibilityModel } from '@packages/fenrir/components';

import type { AssetsTableColumnId } from '../asset-table.constants';

type UseColumnVisibilityModelProps = {
  togglableColumns: AssetsTableColumnId[];
  selectedColumns: AssetsTableColumnId[];
  setSelectedColumns: (newColumns: AssetsTableColumnId[]) => void;
};

/**
 * NOTE: Not using fenrir's usePersistedGrid because:
 * No current way to override config stored in storageManager
 * Need to set config based on queryParams prop as top priority
 */
export const useColumnVisibilityModel = ({
  togglableColumns,
  selectedColumns,
  setSelectedColumns,
}: UseColumnVisibilityModelProps) => {
  const columnVisibilityModel = useMemo(
    () =>
      togglableColumns.reduce<GridColumnVisibilityModel>((acc, column) => {
        acc[column] = selectedColumns.includes(column);

        return acc;
      }, {}),
    [selectedColumns, togglableColumns],
  );

  const setColumnVisibilityModel = useCallback(
    (newVisibilityModel: GridColumnVisibilityModel) => {
      let newColumns: AssetsTableColumnId[];

      const columnEntries = Object.entries(newVisibilityModel);

      /*
        When the user clicks on the "Select all" checkbox, newVisibilityModel prop will be an empty Object {} which makes columnEntries an empty array []
        In this case, we should set all togglable columns as selected
      */
      if (columnEntries.length === 0) {
        newColumns = [...togglableColumns];
      } else
        newColumns = columnEntries.reduce((acc, [key, value]) => {
          if (value) acc.push(key as AssetsTableColumnId);

          return acc;
        }, [] as AssetsTableColumnId[]);

      setSelectedColumns(newColumns);
    },
    [setSelectedColumns, togglableColumns],
  );

  return {
    columnVisibilityModel,
    setColumnVisibilityModel,
  };
};
